import React from 'react';
import { FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Typography,
  Box,
  Grid,
  useTheme,
  Paper,
  Skeleton,
} from '@mui/material';
import Input from '../../../../theme/ui/Atoms/Input/Input';
import Button from '../../../../theme/ui/Atoms/Button/Button';
import { ArrowBackIcon, UploadImageIcon } from '../../../../assets';
import InputColor from '../../../../theme/ui/Atoms/InputColor';
import { useNavigate } from 'react-router-dom';
interface ImageHandlerProps {
  image: string;
  isNewUpload: boolean;
  file?: File;
  error?: string;
}
interface WorkspaceDetailFormProps {
  formik: FormikProps<any>;
  logoImage: ImageHandlerProps | null;
  setLogoImage: React.Dispatch<React.SetStateAction<ImageHandlerProps | null>>;
  imageHandler: (file?: File) => void;
  subworkspaceId?: string;
  quota: {
    users: number;
    devices: number;
  };
  workspaceQuota: {
    users: number;
    devices: number;
  };
}
export const WorkspaceDetailForm = (props: WorkspaceDetailFormProps) => {
  const {
    formik,
    logoImage,
    setLogoImage,
    imageHandler,
    subworkspaceId,
    quota,
    workspaceQuota,
  } = props;

  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <form onSubmit={formik.handleSubmit}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        {t('workspaces.customer_setup')}
      </Typography>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid
          size={{ sm: 8, xs: 12 }}
          sx={{
            borderWidth: 4,
            borderStyle: 'solid',
            borderImage: `${theme.palette.gradientColor.extended} 1`,
            borderRight: { xs: 'none', sm: '4px solid transparent' },
            borderLeft: 'none',
            borderTop: 'none',
            borderBottom: 'none',
            padding: '1rem',
            pr: { sm: 3, xs: 0 },
          }}
        >
          <Stack spacing={3} sx={{ mb: 4 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'start',
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  minWidth: '13rem',
                  fontWeight: 500,
                  paddingTop: '0.8rem',
                  wordBreak: 'break-word',
                }}
              >
                {t('workspaces.name') ?? 'Name'}:
              </Typography>
              <Input
                id="name"
                name="name"
                type="text"
                placeholder={t('workspaces.name_placeholder') ?? ''}
                required
                aria-required
                fullWidth
                value={formik.values.name}
                onChange={formik.handleChange}
                error={Boolean(formik.errors.name)}
                helperText={formik.errors.name || ''}
                sx={{ flex: 1 }}
                disabled={Boolean(subworkspaceId)}
                sxInput={{ minHeight: '2.5rem' }}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'start',
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  minWidth: '13rem',
                  fontWeight: 500,
                  paddingTop: '0.8rem',
                  wordBreak: 'break-word',
                }}
              >
                {t('workspaces.region') ?? 'Region'}:
              </Typography>
              <Input
                id="region"
                name="region"
                type="text"
                placeholder={
                  t('workspaces.region_placeholder') ?? 'Enter region'
                }
                required
                aria-required
                fullWidth
                value={formik.values.region}
                onChange={formik.handleChange}
                error={Boolean(formik.errors.region)}
                helperText={formik.errors.region || ''}
                sxInput={{ minHeight: '2.5rem' }}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'start',
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  minWidth: '13rem',
                  fontWeight: 500,
                  paddingTop: '0.8rem',
                  wordBreak: 'break-word',
                }}
              >
                {t('workspaces.connect_setup') ?? 'Connect Setup'}:
              </Typography>
              <Input
                id="connectSetup"
                name="connectSetup"
                type="text"
                placeholder={
                  t('workspaces.connect_setup_placeholder') ??
                  'Enter connect setup'
                }
                required
                aria-required
                fullWidth
                value={formik.values.connectSetup}
                onChange={formik.handleChange}
                error={Boolean(formik.errors.connectSetup)}
                helperText={formik.errors.connectSetup || ''}
                sxInput={{ minHeight: '2.5rem' }}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'start',
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  minWidth: '13rem',
                  fontWeight: 500,
                  paddingTop: '0.8rem',
                  wordBreak: 'break-word',
                }}
              >
                {t('workspaces.main_contact') ?? 'Main Contact'}:
              </Typography>
              <Input
                id="mainContact"
                name="mainContact"
                type="text"
                placeholder={
                  t('workspaces.main_contact_placeholder') ??
                  'Enter main contact name'
                }
                required
                aria-required
                fullWidth
                value={formik.values.mainContact}
                onChange={formik.handleChange}
                error={Boolean(formik.errors.mainContact)}
                helperText={formik.errors.mainContact || ''}
                sxInput={{ minHeight: '2.5rem' }}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'start',
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  minWidth: '13rem',
                  fontWeight: 500,
                  paddingTop: '0.8rem',
                  wordBreak: 'break-word',
                }}
              >
                {t('workspaces.main_contact_email') ?? 'Main Contact Email'}:
              </Typography>
              <Input
                id="mainContactEmail"
                name="mainContactEmail"
                type="email"
                placeholder={
                  t('workspaces.main_contact_email_placeholder') ??
                  'Enter main contact email'
                }
                required
                aria-required
                fullWidth
                value={formik.values.mainContactEmail}
                onChange={formik.handleChange}
                error={Boolean(formik.errors.mainContactEmail)}
                helperText={formik.errors.mainContactEmail || ''}
                sxInput={{ minHeight: '2.5rem' }}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'start',
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  minWidth: '13rem',
                  fontWeight: 500,
                  paddingTop: '0.8rem',
                  wordBreak: 'break-word',
                }}
              >
                {t('workspaces.number_users') ?? 'Number of Users'}:
              </Typography>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                flexDirection={'row'}
                justifyContent={'center'}
              >
                <Input
                  id="numberUsers"
                  name="numberUsers"
                  type="number"
                  placeholder={
                    t('workspaces.number_users_placeholder') ??
                    'Enter number of users'
                  }
                  required
                  aria-required
                  value={formik.values.numberUsers}
                  onChange={formik.handleChange}
                  error={Boolean(formik.errors.numberUsers)}
                  helperText={formik.errors.numberUsers || ''}
                  sxInput={{ minHeight: '2.5rem' }}
                  InputProps={{
                    min: 0,
                    max: Number(quota.users) || 100,
                  }}
                />
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    wordBreak: 'break-word',
                    lineHeight: 1,
                  }}
                >
                  / {workspaceQuota?.users || 0}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'start',
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  minWidth: '13rem',
                  fontWeight: 500,
                  paddingTop: '0.8rem',
                  wordBreak: 'break-word',
                }}
              >
                {t('workspaces.number_devices') ?? 'Quota'}:
              </Typography>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                flexDirection={'row'}
                justifyContent={'center'}
              >
                <Input
                  id="numberDevices"
                  name="numberDevices"
                  type="number"
                  placeholder={
                    t('workspaces.number_devices_placeholder') ??
                    'Enter number of devices'
                  }
                  required
                  aria-required
                  value={formik.values.numberDevices}
                  onChange={formik.handleChange}
                  error={Boolean(formik.errors.numberDevices)}
                  helperText={formik.errors.numberDevices || ''}
                  sxInput={{ minHeight: '2.5rem' }}
                  InputProps={{
                    min: 0,
                    max: Number(quota.devices) || 100,
                  }}
                />
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    wordBreak: 'break-word',
                    lineHeight: 1,
                  }}
                >
                  / {workspaceQuota?.devices || 0}
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Grid>
        <Grid size={{ sm: 4, xs: 12 }}>
          <Box sx={{ p: 2 }}>
            <input
              type="file"
              accept="image/*"
              id="logo-upload-input"
              onBlur={formik.handleBlur}
              onChange={(e) => {
                const file = e.target.files?.[0];
                imageHandler(file);
              }}
              style={{ display: 'none' }}
            />

            <Box
              onClick={() =>
                document.getElementById('logo-upload-input')?.click()
              }
              sx={{
                borderColor: logoImage?.image ? 'primary.main' : 'divider',
                borderRadius: 2,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s',
                backgroundColor: logoImage?.image
                  ? 'transparent'
                  : 'action.hover',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.selected',
                },
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '300px',
                flexDirection: 'column',
              }}
            >
              {logoImage?.image ? (
                <>
                  <Box
                    component="img"
                    src={logoImage.image}
                    alt="Logo"
                    sx={{
                      width: '100%',
                      borderRadius: 2,
                      height: '100%',
                      objectFit: 'contain',
                      maxHeight: '300px',
                      minHeight: '300px',
                    }}
                  />
                  <Box
                    display={'flex'}
                    flexDirection={'row'}
                    alignItems={'center'}
                    gap={1}
                    width={'100%'}
                    margin={'1rem'}
                  >
                    <Typography variant="body1" sx={{ m: '2rem 0 0 0' }}>
                      {t('workspaces.preview_logo')}
                    </Typography>
                    <Box
                      sx={{
                        width: 'auto',
                        height: '2.5rem',
                        margin: '0 0.25rem 0 0',
                        minWidth: '56px',
                      }}
                    >
                      <img
                        src={logoImage.image}
                        alt="Logo"
                        style={{
                          width: '100%',
                          height: '100%',
                          textAlign: 'center',
                          objectFit: 'cover',
                          color: 'transparent',
                        }}
                      />
                    </Box>
                  </Box>
                </>
              ) : (
                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: !logoImage?.image ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    width: '100%',
                    height: '300px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    backgroundColor: !logoImage?.image
                      ? 'transparent'
                      : 'action.hover',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'action.selected',
                    },
                  }}
                >
                  <UploadImageIcon
                    width={'100%'}
                    height={'100%'}
                    color={theme.palette.common.white}
                  />
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {t('workspaces.upload_logo') ?? 'Click to upload logo'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('workspaces.supported_formats') ?? 'PNG, JPG, GIF'}
                  </Typography>
                </Box>
              )}
            </Box>

            {logoImage?.image && (
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() =>
                    document.getElementById('logo-upload-input')?.click()
                  }
                >
                  {t('workspaces.change_image') ?? 'Change Image'}
                </Button>
                <Button
                  variant="contained-gray"
                  fullWidth
                  onClick={() => {
                    setLogoImage(null);
                    formik.setFieldValue('images.logo', null);
                    formik.setFieldTouched('images.logo', true);
                    (
                      document.getElementById(
                        'logo-upload-input'
                      )! as HTMLInputElement
                    ).value = '';
                  }}
                >
                  {t('button.delete') ?? 'Delete'}
                </Button>
              </Stack>
            )}

            {logoImage?.error && (
              <Typography
                color="error"
                variant="caption"
                sx={{ mt: 1, display: 'block' }}
              >
                {t(logoImage.error) ?? logoImage.error}
              </Typography>
            )}
            <InputColor
              value={formik.values.brandColor}
              label={t('workspaces.select_brand_color') ?? 'Brand Color'}
              onChange={(value: string) =>
                formik.setFieldValue('brandColor', value)
              }
            />
          </Box>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid size={{ sm: 8, xs: 12 }}>
          <Stack
            display={'flex'}
            direction={{ sm: 'row', xs: 'column' }}
            spacing={2}
            sx={{ mt: 4 }}
            justifyContent={'space-between'}
          >
            <Stack
              display="grid"
              gap={2}
              gridTemplateColumns={{ sm: 'repeat(3, 1fr)', xs: '1fr' }}
            >
              <Button
                variant="contained-gray"
                sx={{ height: '3em' }}
                rounded
                fullWidth
                disabled={formik.isSubmitting}
                onClick={() => {
                  if (Boolean(subworkspaceId)) {
                    navigate('../../');
                  } else {
                    navigate('../');
                  }
                }}
              >
                <ArrowBackIcon height={'1em'} />
              </Button>
              <Button
                variant="contained"
                sx={{ height: '3em' }}
                rounded
                fullWidth
                disabled={true}
                onClick={() => {}}
              >
                {t('workspaces.upload_stores')}
              </Button>
              <Button
                variant="contained"
                sx={{ height: '3em' }}
                rounded
                disabled={true}
                fullWidth
                onClick={() => {}}
              >
                {t('workspaces.upload_users')}
              </Button>
            </Stack>

            <Button
              rounded
              variant="contained"
              size="large"
              sx={{ mt: 4, mb: 2, justifySelf: 'start' }}
              type="submit"
              disabled={formik.isSubmitting || !formik.isValid}
              loading={formik.isSubmitting}
            >
              {t('button.save')}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </form>
  );
};

export const WorkspaceDetailFormSkeleton = () => {
  return (
    <Paper elevation={5} sx={{ padding: 3 }}>
      <Skeleton variant="text" width="60%" />

      <Stack spacing={2}>
        {[1, 2, 3].map((item) => (
          <Box
            key={item}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
              display: 'grid',
              gridTemplateColumns: '20% 70% 10%',
              gap: 2,
              alignItems: 'start',
            }}
          >
            <Skeleton variant="text" width="60%" />

            <Box>
              <Skeleton variant="text" width="40%" />
            </Box>
          </Box>
        ))}

        <Skeleton variant="text" width="40%" />
      </Stack>

      <Stack
        display={'flex'}
        direction="row"
        spacing={2}
        sx={{ mt: 4 }}
        justifyContent={'space-evenly'}
      >
        <Skeleton variant="text" width="40%" />
        <Skeleton variant="text" width="60%" />
      </Stack>
    </Paper>
  );
};
